import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Container, Row, Col, Spinner } from 'react-bootstrap'
import { stockItemCategoryAPI } from '../api/stockItemCategoryAPI'
import ResizableTable from '../components/ResizableTable'
import { useTableState } from '../utils/tableUtils'
import { highlightText } from '../utils/commonUtils'
import { handleSearchWithDebounce } from '../utils/tableUtils'
import PaginationComponent from '../components/Pagination'
import { usePermissions } from '../contexts/PermissionContext'
import SearchInputWithClear from '../components/SearchInputWithClear'

function StockItemCategoriesListScreen() {
    const { hasPermission } = usePermissions()
    const [categories, setCategories] = useState([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const debounceTimeoutRef = useRef(null)
    const navigate = useNavigate()

    const initialTableState = {
        visibility: {
            name: true,
            actions: true
        },
        widths: {
            name: 'auto',
            actions: 100
        }
    };

    const { columnState } = useTableState('stock_item_categories', initialTableState);

    useEffect(() => {
        async function fetchCategories() {
            try {
                setLoading(true)
                const response = await stockItemCategoryAPI.getAll(currentPage, pageSize, searchTerm)
                setCategories(response.results)
                setTotalPages(response.count)
            } catch (error) {
                console.error('Error fetching categories:', error)
            } finally {
                setLoading(false)
            }
        }
        fetchCategories()
    }, [currentPage, pageSize, searchTerm])

    const handleClearSearch = () => {
        setSearchInputValue('');
        setSearchTerm('');
        setCurrentPage(1);
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInputValue(value);
        handleSearchWithDebounce(
            value,
            setSearchTerm,
            setCurrentPage,
            debounceTimeoutRef
        );
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const columns = [
        {
            id: 'name',
            header: 'Name',
            cell: (row) => highlightText(row.name, searchTerm)
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: (row) => (
                hasPermission('stock.categories.modify') && (
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation()
                            handleDelete(row._id)
                        }}
                    >
                        Delete
                    </Button>
                )
            )
        }
    ]

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this category?')) {
            try {
                await stockItemCategoryAPI.delete(id)
                setCategories(categories.filter(category => category._id !== id))
            } catch (error) {
                console.error('Error deleting category:', error)
                alert('Failed to delete category')
            }
        }
    }

    return (
        <Container className="py-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h1>Stock Item Categories</h1>
                </Col>
                <Col md={4}>
                    <SearchInputWithClear
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        onClear={handleClearSearch}
                        placeholder="Search categories..."
                        autoComplete="off"
                        spellCheck="false"
                    />
                </Col>
                {hasPermission('stock.categories.modify') && (
                    <Col xs="auto">
                        <Button
                            onClick={() => navigate('/stock-item-category/create')}
                            variant="success"
                        >
                            Create Stock Item Category
                        </Button>
                    </Col>
                )}
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            ) : (
                <>
                    <ResizableTable
                        columns={columns}
                        data={categories}
                        onRowClick={(category) => navigate(`/stock-item-category/${category._id}`)}
                        initialColumnWidths={columnState.widths}
                        tableId="stock_item_categories"
                        striped
                        hover
                    />
                    <PaginationComponent
                        currentPage={currentPage}
                        totalItems={totalPages}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={setPageSize}
                    />
                </>
            )}
        </Container>
    )
}

export default StockItemCategoriesListScreen